import type { ColorSemantico } from "../../shared/types";

// Resolver único de color semántico: un identificador controlado (ver
// COLORES_SEMANTICOS en shared/types.ts) siempre se traduce a las MISMAS
// clases/hex acá, para que un concepto (ej. Materia "Laboral") se vea igual
// en toda la app. Las clases son literales completas (no template strings
// con variables) para que el JIT de Tailwind las detecte al escanear este
// archivo. Nunca acepta ni concatena un valor fuera de esta tabla.
interface ClasesColor {
  punto: string;
  texto: string;
  fondoSuave: string;
  borde: string;
  barra: string;
  chip: string;
  icono: string;
  hex: string;
}

const PALETA: Record<ColorSemantico, ClasesColor> = {
  red: {
    punto: "bg-red-500",
    texto: "text-red-700",
    fondoSuave: "bg-red-50",
    borde: "border-red-200",
    barra: "bg-red-500",
    chip: "bg-red-50 text-red-700 border border-red-200",
    icono: "text-red-600",
    hex: "#ef4444",
  },
  green: {
    punto: "bg-green-500",
    texto: "text-green-700",
    fondoSuave: "bg-green-50",
    borde: "border-green-200",
    barra: "bg-green-500",
    chip: "bg-green-50 text-green-700 border border-green-200",
    icono: "text-green-600",
    hex: "#22c55e",
  },
  blue: {
    punto: "bg-blue-500",
    texto: "text-blue-700",
    fondoSuave: "bg-blue-50",
    borde: "border-blue-200",
    barra: "bg-blue-500",
    chip: "bg-blue-50 text-blue-700 border border-blue-200",
    icono: "text-blue-600",
    hex: "#3b82f6",
  },
  violet: {
    punto: "bg-violet-500",
    texto: "text-violet-700",
    fondoSuave: "bg-violet-50",
    borde: "border-violet-200",
    barra: "bg-violet-500",
    chip: "bg-violet-50 text-violet-700 border border-violet-200",
    icono: "text-violet-600",
    hex: "#8b5cf6",
  },
  orange: {
    punto: "bg-orange-500",
    texto: "text-orange-700",
    fondoSuave: "bg-orange-50",
    borde: "border-orange-200",
    barra: "bg-orange-500",
    chip: "bg-orange-50 text-orange-700 border border-orange-200",
    icono: "text-orange-600",
    hex: "#f97316",
  },
  amber: {
    punto: "bg-amber-500",
    texto: "text-amber-700",
    fondoSuave: "bg-amber-50",
    borde: "border-amber-200",
    barra: "bg-amber-500",
    chip: "bg-amber-50 text-amber-700 border border-amber-200",
    icono: "text-amber-600",
    hex: "#f59e0b",
  },
  cyan: {
    punto: "bg-cyan-500",
    texto: "text-cyan-700",
    fondoSuave: "bg-cyan-50",
    borde: "border-cyan-200",
    barra: "bg-cyan-500",
    chip: "bg-cyan-50 text-cyan-700 border border-cyan-200",
    icono: "text-cyan-600",
    hex: "#06b6d4",
  },
  slate: {
    punto: "bg-slate-400",
    texto: "text-slate-700",
    fondoSuave: "bg-slate-50",
    borde: "border-slate-200",
    barra: "bg-slate-400",
    chip: "bg-slate-50 text-slate-700 border border-slate-200",
    icono: "text-slate-600",
    hex: "#64748b",
  },
};

const FALLBACK = PALETA.slate;

export function resolverColorSemantico(id: string | null | undefined): ClasesColor {
  if (id && Object.prototype.hasOwnProperty.call(PALETA, id)) {
    return PALETA[id as ColorSemantico];
  }
  return FALLBACK;
}

// Asignación visual estable para "Abogado" en gráficos/rankings — sin tocar
// permisos ni identidad del usuario: nada se guarda, es un hash puro sobre
// el id, siempre da el mismo color para el mismo usuario.
const ORDEN_COLORES = Object.keys(PALETA) as ColorSemantico[];

export function colorParaUsuario(usuarioId: string): ColorSemantico {
  let suma = 0;
  for (let i = 0; i < usuarioId.length; i++) suma += usuarioId.charCodeAt(i);
  return ORDEN_COLORES[suma % ORDEN_COLORES.length];
}

// Mapas centralizados de valor textual → color semántico para las escalas
// fijas de Riesgo/Probabilidad — un único punto de verdad usado tanto por
// Insignia.tsx (badges) como por los gráficos del Dashboard Judicial
// (GraficoDona/GraficoBarras), para que "Alto"/"Crítico" se vean con el
// mismo color en toda la app.
export const COLOR_POR_RIESGO: Record<string, ColorSemantico> = {
  Bajo: "green",
  Medio: "amber",
  Alto: "orange",
  "Crítico": "red",
};

export const COLOR_POR_PROBABILIDAD: Record<string, ColorSemantico> = {
  "Muy baja": "red",
  Baja: "orange",
  Media: "amber",
  Alta: "green",
  "Muy alta": "green",
};

// Mi Trabajo — calendario: los 5 "tipos" mostrados se derivan de columnas de
// texto libre ya existentes (actuaciones.tipo, eventos_agenda.tipo_evento),
// nunca de una columna/tabla nueva. Un color fijo por tipo para que el
// calendario se lea de un vistazo.
export const COLOR_POR_TIPO_EVENTO_TRABAJO: Record<string, ColorSemantico> = {
  Audiencia: "violet",
  Vencimiento: "red",
  Escrito: "blue",
  Tarea: "amber",
  Recordatorio: "cyan",
};

export const COLOR_POR_DISPONIBILIDAD: Record<string, ColorSemantico> = {
  Disponible: "green",
  "Riesgo Medio": "amber",
  "No recomendable": "red",
};

// Carga de trabajo por abogado (Mi Trabajo → Carga del equipo): lectura
// visual, no una medición objetiva de capacidad — ver
// calcularNivelCarga() en src/modules/mi-trabajo/colaTrabajo.ts. Mismos 4
// niveles que ya usa el resto de la app para escalas de severidad
// (Disponible/Riesgo/No recomendable), sólo con una etiqueta más.
export const COLOR_POR_NIVEL_CARGA: Record<string, ColorSemantico> = {
  Baja: "green",
  Media: "amber",
  Alta: "orange",
  Sobrecargada: "red",
};
